import { useCallback, useEffect, useRef, useState } from 'react';
import { hasModernHubspotScript, loadLegacyHubspotScript, loadModernHubspotScript, } from './hubspotScriptLoader';
import { syncHubspotFields, } from './syncHubspotFields';
const getHubSpotFormsV4 = () => typeof window === 'undefined'
    ? undefined
    : window.HubSpotFormsV4;
const findRenderedForm = (target, root, hubspotId) => {
    const selectors = [`form[data-form-id="${hubspotId}"]`, 'form'];
    const queryWithin = (node) => {
        if (!node)
            return null;
        for (const selector of selectors) {
            const form = node.querySelector(selector);
            if (form) {
                return form;
            }
        }
        return null;
    };
    if (target instanceof HTMLFormElement) {
        return target;
    }
    if (target instanceof Element) {
        if (target.matches('form')) {
            return target;
        }
        const closestForm = target.closest('form');
        if (closestForm instanceof HTMLFormElement) {
            return closestForm;
        }
        const nestedForm = queryWithin(target);
        if (nestedForm) {
            return nestedForm;
        }
    }
    return queryWithin(root);
};
const useHubspotContainerRegistration = ({ enabled, formElement, hubspotId, }) => {
    useEffect(() => {
        if (!enabled) {
            return;
        }
        let isDisposed = false;
        const registerContainer = async () => {
            try {
                await loadModernHubspotScript();
            }
            catch (error) {
                void error;
                return;
            }
            if (isDisposed) {
                return;
            }
            const container = formElement.current;
            if (!(container === null || container === void 0 ? void 0 : container.parentNode) || !hasModernHubspotScript()) {
                return;
            }
            const parent = container.parentNode;
            const nextSibling = container.nextSibling;
            parent.removeChild(container);
            if (nextSibling) {
                parent.insertBefore(container, nextSibling);
            }
            else {
                parent.appendChild(container);
            }
        };
        void registerContainer();
        return () => {
            isDisposed = true;
        };
    }, [enabled, formElement, hubspotId]);
};
const useLegacyHubspotFormLoader = ({ connectForm, enabled, formElement, formId, hubspotId, resetConnectedForm, setIsLoading, }) => {
    useEffect(() => {
        if (!enabled) {
            return;
        }
        let isDisposed = false;
        const targetNode = formElement.current;
        const createLegacyForm = async () => {
            var _a, _b;
            setIsLoading(true);
            try {
                await loadLegacyHubspotScript();
            }
            catch (error) {
                void error;
                if (!isDisposed) {
                    setIsLoading(false);
                }
                return;
            }
            if (isDisposed) {
                return;
            }
            if (!(targetNode === null || targetNode === void 0 ? void 0 : targetNode.id) || !((_b = (_a = window.hbspt) === null || _a === void 0 ? void 0 : _a.forms) === null || _b === void 0 ? void 0 : _b.create)) {
                setIsLoading(false);
                return;
            }
            targetNode.innerHTML = '';
            window.hbspt.forms.create({
                region: 'na1',
                portalId: '21902860',
                formId: hubspotId,
                target: `#${formId}`,
                onFormReady(form) {
                    if (isDisposed) {
                        return;
                    }
                    connectForm(form);
                },
            });
        };
        void createLegacyForm();
        return () => {
            isDisposed = true;
            resetConnectedForm();
            if (targetNode) {
                targetNode.innerHTML = '';
            }
        };
    }, [
        connectForm,
        enabled,
        formElement,
        formId,
        hubspotId,
        resetConnectedForm,
        setIsLoading,
    ]);
};
const useModernHubspotFormLoader = ({ connectForm, enabled, formElement, hubspotId, resetConnectedForm, setIsLoading, }) => {
    useHubspotContainerRegistration({ enabled, formElement, hubspotId });
    useEffect(() => {
        if (!enabled) {
            return;
        }
        const targetNode = formElement.current;
        if (!targetNode) {
            return;
        }
        setIsLoading(true);
        let observer;
        const connectRenderedForm = () => {
            const form = targetNode.querySelector(`form[data-form-id="${hubspotId}"], form`);
            if (!form) {
                return false;
            }
            connectForm(form);
            observer === null || observer === void 0 ? void 0 : observer.disconnect();
            observer = undefined;
            return true;
        };
        const listener = (event) => {
            var _a, _b, _c;
            const formInstance = (_b = (_a = getHubSpotFormsV4()) === null || _a === void 0 ? void 0 : _a.getFormFromEvent) === null || _b === void 0 ? void 0 : _b.call(_a, event);
            if (((_c = formInstance === null || formInstance === void 0 ? void 0 : formInstance.getFormId) === null || _c === void 0 ? void 0 : _c.call(formInstance)) !== hubspotId) {
                return;
            }
            const form = findRenderedForm(event.target, targetNode, hubspotId);
            if (!form) {
                return;
            }
            connectForm(form, formInstance);
        };
        window.addEventListener('hs-form-event:on-ready', listener);
        if (!connectRenderedForm()) {
            observer = new MutationObserver(() => {
                connectRenderedForm();
            });
            observer.observe(targetNode, {
                childList: true,
                subtree: true,
            });
        }
        return () => {
            window.removeEventListener('hs-form-event:on-ready', listener);
            observer === null || observer === void 0 ? void 0 : observer.disconnect();
            resetConnectedForm();
        };
    }, [
        connectForm,
        enabled,
        formElement,
        hubspotId,
        resetConnectedForm,
        setIsLoading,
    ]);
};
const useHubspotFormLoader = ({ formId, hubspotId, additionalFields, isLegacy = false, }) => {
    const formElement = useRef(null);
    const [isLoading, setIsLoading] = useState(true);
    const connectedFormRef = useRef(null);
    const connectedFormInstanceRef = useRef(undefined);
    const disconnectConnectedFormRef = useRef(undefined);
    const additionalFieldsRef = useRef(additionalFields !== null && additionalFields !== void 0 ? additionalFields : {});
    const syncedFieldNamesRef = useRef(new Set(Object.keys(additionalFields !== null && additionalFields !== void 0 ? additionalFields : {})));
    const resetConnectedForm = useCallback(() => {
        var _a;
        (_a = disconnectConnectedFormRef.current) === null || _a === void 0 ? void 0 : _a.call(disconnectConnectedFormRef);
        disconnectConnectedFormRef.current = undefined;
        connectedFormRef.current = null;
        connectedFormInstanceRef.current = undefined;
    }, []);
    const buildSyncFields = useCallback((fields = additionalFieldsRef.current) => {
        const fieldNames = new Set([
            ...syncedFieldNamesRef.current,
            ...Object.keys(fields),
        ]);
        const syncedFields = {};
        fieldNames.forEach((name) => {
            var _a;
            syncedFields[name] = (_a = fields[name]) !== null && _a !== void 0 ? _a : '';
        });
        syncedFieldNamesRef.current = new Set(Object.keys(fields));
        return syncedFields;
    }, []);
    const syncAdditionalFields = useCallback((form, formInstance, fields = additionalFieldsRef.current) => {
        const nextFields = buildSyncFields(fields);
        if (!Object.keys(nextFields).length) {
            return;
        }
        syncHubspotFields({
            fields: nextFields,
            form,
            formInstance,
        });
    }, [buildSyncFields]);
    const connectForm = useCallback((form, formInstance) => {
        if (connectedFormRef.current === form) {
            if (formInstance) {
                connectedFormInstanceRef.current = formInstance;
            }
            syncAdditionalFields(form, connectedFormInstanceRef.current);
            setIsLoading(false);
            return;
        }
        resetConnectedForm();
        connectedFormRef.current = form;
        connectedFormInstanceRef.current = formInstance;
        syncAdditionalFields(form, connectedFormInstanceRef.current);
        const handleSubmit = () => {
            syncAdditionalFields(form, connectedFormInstanceRef.current);
        };
        form.addEventListener('submit', handleSubmit, true);
        disconnectConnectedFormRef.current = () => {
            form.removeEventListener('submit', handleSubmit, true);
        };
        setIsLoading(false);
    }, [resetConnectedForm, syncAdditionalFields]);
    useEffect(() => {
        additionalFieldsRef.current = additionalFields !== null && additionalFields !== void 0 ? additionalFields : {};
        if (connectedFormRef.current) {
            syncAdditionalFields(connectedFormRef.current, connectedFormInstanceRef.current);
            return;
        }
        syncedFieldNamesRef.current = new Set(Object.keys(additionalFieldsRef.current));
    }, [additionalFields, syncAdditionalFields]);
    useEffect(() => {
        syncedFieldNamesRef.current = new Set(Object.keys(additionalFieldsRef.current));
    }, [formId, hubspotId, isLegacy]);
    useEffect(() => {
        return () => {
            resetConnectedForm();
        };
    }, [resetConnectedForm]);
    useLegacyHubspotFormLoader({
        connectForm,
        enabled: isLegacy,
        formElement,
        formId,
        hubspotId,
        resetConnectedForm,
        setIsLoading,
    });
    useModernHubspotFormLoader({
        connectForm,
        enabled: !isLegacy,
        formElement,
        hubspotId,
        resetConnectedForm,
        setIsLoading,
    });
    return { formElement, isLoading };
};
export default useHubspotFormLoader;
